import { useT } from '@/i18n';
import { Chip, Field, Input, Select, TextArea } from '@/components/ui';
import { asksConditions, CONDITIONS, GRADES, SCHOOLS, STATUSES, THERAPIES, toggle, type AboutState } from './aboutForm';
import { ProfileProgress } from './ProfileProgress';

/**
 * The "about my child" questions, shared by the profile page (saved on its own) and the add-child
 * form (asked up front so parents don't miss them). Controlled; the host owns the state and saving.
 */
export const AboutFields = ({ state, onChange, childName }: { state: AboutState; onChange: (patch: Partial<AboutState>) => void; childName: string }) => {
  const t = useT();
  const withConditions = asksConditions(state.status);
  // A class typed before this was a dropdown stays selectable.
  const customGrade = state.grade && !GRADES.some((g) => g.value === state.grade) ? state.grade : undefined;
  return (
    <>
      <ProfileProgress state={state} childName={childName} />

      <fieldset>
        <legend className="mb-2 text-label text-ink">{t('about.diagnosis')}</legend>
        <div role="radiogroup" aria-label={t('about.diagnosis')} className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <Chip key={s} role="radio" selected={state.status === s} onToggle={() => onChange({ status: s })}>
              {t(`about.status.${s}`)}
            </Chip>
          ))}
        </div>
      </fieldset>

      {withConditions && (
        <fieldset>
          <legend className="mb-2 text-label text-ink">{state.status === 'waiting' ? t('about.conditionsWaiting') : t('about.conditions')}</legend>
          <div className="flex flex-wrap gap-2">
            {CONDITIONS.map((c) => (
              <Chip key={c} selected={state.conditions.includes(c)} onToggle={() => onChange({ conditions: toggle(state.conditions, c) })}>
                {t(`about.condition.${c}`)}
              </Chip>
            ))}
          </div>
          {state.conditions.includes('other') && (
            <Field id="about-other" label={t('about.other')} className="mt-3">
              <Input value={state.other} maxLength={80} onChange={(e) => onChange({ other: e.target.value })} />
            </Field>
          )}
        </fieldset>
      )}

      <fieldset>
        <legend className="mb-2 text-label text-ink">{t('about.therapies')}</legend>
        <div className="flex flex-wrap gap-2">
          {THERAPIES.map((th) => (
            <Chip key={th} selected={state.therapies.includes(th)} onToggle={() => onChange({ therapies: toggle(state.therapies, th) })}>
              {t(`about.therapy.${th}`)}
            </Chip>
          ))}
        </div>
      </fieldset>

      <fieldset className="grid gap-3">
        <legend className="mb-2 text-label text-ink">{t('about.school')}</legend>
        <div role="radiogroup" aria-label={t('about.school')} className="flex flex-wrap gap-2">
          {SCHOOLS.map((s) => (
            <Chip key={s} role="radio" selected={state.school === s} onToggle={() => onChange({ school: state.school === s ? undefined : s })}>
              {t(`about.schoolSetting.${s}`)}
            </Chip>
          ))}
        </div>
        {state.school && state.school !== 'not_yet' && (
          <Field id="about-grade" label={t('about.grade')} className="max-w-[16rem]">
            <Select value={state.grade} onChange={(e) => onChange({ grade: e.target.value })}>
              <option value="">{t('about.grade.choose')}</option>
              {customGrade && <option value={customGrade}>{customGrade}</option>}
              {GRADES.map((g) => (
                <option key={g.value} value={g.value}>
                  {t(g.key, g.n ? { n: g.n } : undefined)}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </fieldset>

      <Field id="about-languages" label={t('about.languages')} hint={t('about.languagesHint')}>
        <Input value={state.languages} maxLength={200} onChange={(e) => onChange({ languages: e.target.value })} />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field id="about-loves" label={t('about.loves', { child: childName })}>
          <TextArea value={state.loves} maxLength={300} rows={3} onChange={(e) => onChange({ loves: e.target.value })} />
        </Field>
        <Field id="about-upsets" label={t('about.upsets', { child: childName })}>
          <TextArea value={state.upsets} maxLength={300} rows={3} onChange={(e) => onChange({ upsets: e.target.value })} />
        </Field>
      </div>
    </>
  );
};
